import React from 'react'
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'
import './DashboardStackedBarChart.style.css'
import { DASHBOARD_BAR_TOOLTIP_CURSOR } from './dashboardRecharts.constants'

/** Data point for stacked bar chart. Keys beyond `label` are series (e.g. patient, insurance). */
export type DashboardStackedBarChartDataPoint = Record<string, string | number>

export type DashboardStackedBarChartSeries = {
  dataKey: string
  name: string
  color: string
}

/** Optional: map category label to color for the small circle and bar (Figma style). */
export type CategoryIndicatorColors = Record<string, string>

/** Tooltip for collapsed grouped mode: show time slot and each provider's open minutes from _bars. */
function CollapsedGroupedTooltipContent({
  active,
  payload,
  valueFormatter,
}: {
  active?: boolean
  payload?: Array<{
    payload?: {
      label?: string
      _bars?: Array<{ name: string; value: number; color: string }>
    }
  }>
  valueFormatter: (v: number) => string
}): React.ReactElement | null {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  const bars = row?._bars ?? []
  const label = row?.label ?? ''
  return (
    <div
      className="dashboard-stacked-bar-chart-tooltip"
      style={{
        padding: '8px 12px',
        fontSize: 12,
        borderRadius: 8,
        border: '1px solid var(--gray-200)',
        background: 'var(--gray-white)',
      }}
    >
      {label != null && label !== '' && (
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      )}
      {bars.map((b) => (
        <div
          key={b.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: b.color,
              flexShrink: 0,
            }}
          />
          <span>
            {b.name}: {valueFormatter(b.value)}
          </span>
        </div>
      ))}
      <div
        style={{
          marginTop: 6,
          paddingTop: 6,
          borderTop: '1px solid var(--gray-200)',
          fontSize: 11,
          color: 'var(--gray-500)',
        }}
      >
        No bar = fully booked for this slot.
      </div>
    </div>
  )
}

/** Custom legend for collapsed grouped mode (single Bar, so Recharts has no series to show). */
function CollapsedGroupedLegend({
  series,
  iconType = 'circle',
}: {
  series: DashboardStackedBarChartSeries[]
  iconType?: 'circle' | 'square'
}): React.ReactElement {
  return (
    <div className="dashboard-stacked-bar-chart-legend dashboard-stacked-bar-chart-legend--collapsed">
      {series.map((s) => (
        <span
          key={s.dataKey}
          className="dashboard-stacked-bar-chart-legend-item"
        >
          <span
            className="dashboard-stacked-bar-chart-legend-icon"
            style={{
              background: s.color,
              borderRadius: iconType === 'circle' ? '50%' : 2,
              width: 10,
              height: 10,
              display: 'inline-block',
              marginRight: 6,
              verticalAlign: 'middle',
            }}
          />
          <span>{s.name}</span>
        </span>
      ))}
    </div>
  )
}

/** Tooltip for single-color AR bars: shows Patient AR and Insurance AR from payload. */
function ARTooltipContent({
  active,
  payload,
  label,
  valueFormatter,
}: {
  active?: boolean
  payload?: Array<{ payload?: DashboardStackedBarChartDataPoint }>
  label?: string
  valueFormatter: (v: number) => string
}): React.ReactElement | null {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  const hasBreakdown = row && 'patient' in row && 'insurance' in row
  return (
    <div
      style={{
        padding: '8px 12px',
        fontSize: 12,
        borderRadius: 8,
        border: '1px solid var(--gray-200)',
        background: 'var(--gray-white)',
      }}
    >
      {label != null && (
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      )}
      {hasBreakdown && row ? (
        <>
          <div>Insurance AR: {valueFormatter(Number(row.insurance))}</div>
          <div>Patient AR: {valueFormatter(Number(row.patient))}</div>
        </>
      ) : (
        <div>
          Total:{' '}
          {valueFormatter(Number((row as Record<string, unknown>)?.total ?? 0))}
        </div>
      )}
    </div>
  )
}

type DashboardStackedBarChartProps = {
  /** One item per row/category (e.g. aging bucket). Must include `label` and each series dataKey. */
  data: DashboardStackedBarChartDataPoint[]
  /** Series definitions; vertical stack draw order follows array order. */
  series: DashboardStackedBarChartSeries[]
  /** If true, bars are horizontal (category on Y axis). Default true for AR-style charts. */
  horizontal?: boolean
  /** Optional: colors for the small circle and bar (one color per category). When set, shows one bar per category with that color and hides legend. */
  categoryIndicatorColors?: CategoryIndicatorColors
  /** When true and categoryIndicatorColors is set, hide the circle beside axis labels (e.g. when using an external legend). */
  hideCategoryIndicatorCircles?: boolean
  /** When true (and horizontal=false), hide category axis labels completely (useful when showing an external legend). */
  hideCategoryAxisLabels?: boolean
  /** Optional: called when a bar is clicked (e.g. to show Patient AR / Insurance AR detail). */
  onBarClick?: (payload: DashboardStackedBarChartDataPoint) => void
  /** Format axis/tooltip values (e.g. currency). */
  valueFormatter?: (value: number) => string
  /** Height of the chart container. Defaults to 200. Used as min-height when fillHeight is true. */
  height?: number
  /** When true, chart fills the height of its flex parent (set min-height from height prop). */
  fillHeight?: boolean
  /** Optional: custom Y-axis domain [min, max] when horizontal=false. */
  yAxisDomain?: [number, number]
  /** Optional: custom Y-axis tick values when horizontal=false (e.g. [0, 30, 60, 120]). */
  yAxisTicks?: number[]
  /** Optional: margin override (e.g. { bottom: 32 } for more x-axis label space). */
  margin?: { top?: number; right?: number; left?: number; bottom?: number }
  /** Optional: XAxis tick interval when horizontal=false (e.g. 1 = show every 2nd label). */
  xAxisInterval?: number
  /** Optional: YAxis width when horizontal=false (e.g. 56 for "0 hr" / "120 mins" labels to avoid overlap). */
  yAxisWidth?: number
  /** Optional: gap between Y-axis line and tick labels when horizontal=false (Recharts default adds noticeable left gutter). */
  yAxisTickMargin?: number
  /** Optional: gap between category bars and X-axis labels when horizontal=false. */
  xAxisTickMargin?: number
  /** If false, bars are grouped (one bar per series per category) instead of stacked. Default true. */
  stacked?: boolean
  /** Gap between bars in the same category (grouped bars). e.g. 0 for no space. */
  barGap?: number
  /** Gap between categories (e.g. '10%' or 16). Helps center bar group under each label. */
  barCategoryGap?: number | string
  /** Legend icon shape: 'circle' | 'square'. Default 'circle'. */
  legendIconType?: 'circle' | 'square'
  /** When horizontal: optional X-axis domain [min, max] (e.g. [0, 100] for %). */
  xAxisDomain?: [number, number]
  /** When horizontal: optional X-axis tick values (e.g. [0, 25, 50, 75, 100]). */
  xAxisTicks?: number[]
  /** When horizontal: draw a vertical goal line at this value (e.g. 10 for 10%). */
  goalLineValue?: number
  /** Show built-in legend. Default true. Set false when using custom legend (e.g. No Show with Goal). */
  showLegend?: boolean
  /** Optional: bar width in px when vertical (horizontal=false). Overrides default 64. */
  barSize?: number
  /** When true and grouped (non-stacked) vertical bars: hide zero-value bars (draw transparent) so layout stays centered. */
  hideZeroBars?: boolean
  /** When true with grouped vertical bars and hideZeroBars: draw only non-zero bars per category, packed side-by-side with no gap. */
  collapseZeroBarsInGrouped?: boolean
  /** When true with grouped vertical bars: round top corners only (Figma bar style). */
  roundBarTopOnly?: boolean
  className?: string
}

/** Min height (px) for zero-value bars in grouped mode so the slot is visible and layout stays centered. */
const ZERO_BAR_MIN_HEIGHT = 8
const ZERO_BAR_FILL = 'var(--gray-100)'

/** SVG path for a vertical bar with rounded top corners only. */
function verticalBarTopRoundedPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): string {
  const r = Math.min(radius, width / 2, height)
  if (r <= 0 || height <= 0) {
    return `M ${x},${y} h ${width} v ${height} h ${-width} Z`
  }
  return [
    `M ${x},${y + height}`,
    `L ${x},${y + r}`,
    `Q ${x},${y} ${x + r},${y}`,
    `L ${x + width - r},${y}`,
    `Q ${x + width},${y} ${x + width},${y + r}`,
    `L ${x + width},${y + height}`,
    'Z',
  ].join(' ')
}

function isTopOnlyBarRadius(radius: number | number[] | undefined): boolean {
  if (!Array.isArray(radius) || radius.length < 4) return false
  return radius[0] > 0 && radius[1] > 0 && radius[2] === 0 && radius[3] === 0
}

/** Custom bar shape for grouped bars: show a small gray bar when value is 0 so all 6 slots are visible. */
function GroupedBarShape(props: unknown): React.ReactElement {
  const p = props as {
    x?: number
    y?: number
    width?: number
    height?: number
    value?: number
    fill?: string
    radius?: number | number[]
  }
  const { x = 0, y = 0, width = 0, height = 0, value, fill = '', radius } = p
  const isZero = value === 0 || value == null || height === 0
  const rad = Array.isArray(radius) ? radius : [0, 0, 0, 0]
  const topOnly = isTopOnlyBarRadius(radius)
  const cornerR = rad[0] ?? 0
  if (isZero) {
    const top = y - ZERO_BAR_MIN_HEIGHT
    return (
      <rect
        x={x}
        y={top}
        width={width}
        height={ZERO_BAR_MIN_HEIGHT}
        fill={ZERO_BAR_FILL}
        rx={topOnly ? cornerR : 8}
        ry={topOnly ? cornerR : 8}
      />
    )
  }
  if (topOnly) {
    return (
      <path
        d={verticalBarTopRoundedPath(x, y, width, height, cornerR)}
        fill={fill}
      />
    )
  }
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      rx={cornerR}
      ry={cornerR}
    />
  )
}

/** Grouped bar shape that hides zero-value bars (transparent) so bars stay centered. */
function HideZeroGroupedBarShape(props: unknown): React.ReactElement {
  const p = props as {
    x?: number
    y?: number
    width?: number
    height?: number
    value?: number
    fill?: string
    radius?: number | number[]
  }
  const { x = 0, y = 0, width = 0, height = 0, value, fill = '', radius } = p
  const isZero = value === 0 || value == null || height === 0
  const rad = Array.isArray(radius) ? radius : [0, 0, 0, 0]
  const topOnly = isTopOnlyBarRadius(radius)
  const cornerR = rad[0] ?? 0
  if (isZero) {
    return <rect x={x} y={y} width={width} height={0} fill="transparent" />
  }
  if (topOnly) {
    return (
      <path
        d={verticalBarTopRoundedPath(x, y, width, height, cornerR)}
        fill={fill}
      />
    )
  }
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      rx={cornerR}
      ry={cornerR}
    />
  )
}

const COLLAPSED_BAR_GAP = 0

/** Draw only non-zero bars per category, packed side-by-side and centered. */
function CollapsedGroupedBarShape(props: unknown): React.ReactElement {
  const p = props as {
    x?: number
    y?: number
    width?: number
    height?: number
    payload?: {
      value?: number
      _bars?: Array<{
        value: number
        color: string
        dataKey: string
        name: string
      }>
    }
  }
  const { x = 0, y = 0, width = 0, height = 0, payload } = p
  const bars = payload?._bars ?? []
  const maxVal = Number(payload?.value) || 1
  if (bars.length === 0) return <g />
  const n = bars.length
  const barW = width > 0 ? (width - (n - 1) * COLLAPSED_BAR_GAP) / n : 28
  const totalW = n * barW + (n - 1) * COLLAPSED_BAR_GAP
  const startX = x + Math.max(0, (width - totalW) / 2)
  const rad = 8
  const baseY = y + height
  return (
    <g>
      {bars.map((b, i) => {
        const bx = startX + i * (barW + COLLAPSED_BAR_GAP)
        const barHeight = maxVal > 0 ? (b.value / maxVal) * height : 0
        const barY = baseY - barHeight
        return (
          <rect
            key={b.dataKey}
            x={bx}
            y={barY}
            width={barW}
            height={barHeight}
            fill={b.color}
            rx={rad}
            ry={rad}
          />
        )
      })}
    </g>
  )
}

function HorizontalCategoryAxisTick({
  x = 0,
  y = 0,
  payload,
}: {
  x?: number
  y?: number
  payload?: { value?: string }
}): React.ReactElement {
  const raw = String(payload?.value ?? '')
  const label = raw.length > 38 ? `${raw.slice(0, 37)}…` : raw

  return (
    <text
      x={x}
      y={y}
      dy="0.355em"
      textAnchor="end"
      fill="var(--gray-500)"
      fontSize={12}
      fontWeight={500}
    >
      <title>{raw}</title>
      {label}
    </text>
  )
}

/** Custom X-axis tick: optional circle (when categoryIndicatorColors and not hidden) + label. */
function CategoryTick({
  payload,
  x,
  y,
  categoryIndicatorColors,
  hideCircle,
}: {
  payload?: { value?: string } | { value?: string }[]
  x?: number
  y?: number
  categoryIndicatorColors?: CategoryIndicatorColors
  hideCircle?: boolean
}): React.ReactElement | null {
  const raw = Array.isArray(payload) ? payload[0] : payload
  const value = raw?.value
  if (value == null || x == null || y == null) return null
  const showCircle = categoryIndicatorColors != null && !hideCircle
  const fill =
    categoryIndicatorColors?.[value] ??
    categoryIndicatorColors?.[value.replace(/\s*days?$/i, '')] ??
    'var(--gray-400)'
  const radius = 4
  const circleToLabelGap = 8
  /* Circle to the right of the label to avoid overlap with adjacent labels. */
  const circleCx = 36 + circleToLabelGap + radius
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fill="var(--gray-500)"
      >
        {value}
      </text>
      {showCircle && (
        <circle
          cx={circleCx}
          cy={0}
          r={radius}
          fill={fill}
          stroke="var(--gray-200)"
          strokeWidth={1}
        />
      )}
    </g>
  )
}

const STACKED_BAR_CORNER_RADIUS = 8
const VERTICAL_BAR_RADIUS_TOP: [number, number, number, number] = [8, 8, 0, 0]
const VERTICAL_BAR_RADIUS_ALL: [number, number, number, number] = [8, 8, 8, 8]

/** Recharts radius order: [topLeft, topRight, bottomRight, bottomLeft]. */
function getStackedBarRadius(
  seriesIndex: number,
  seriesCount: number,
  horizontal: boolean,
): [number, number, number, number] {
  const r = STACKED_BAR_CORNER_RADIUS
  const isFirst = seriesIndex === 0
  const isLast = seriesIndex === seriesCount - 1
  if (seriesCount === 1) return [r, r, r, r]
  if (horizontal) {
    if (isFirst) return [r, 0, 0, r]
    if (isLast) return [0, r, r, 0]
    return [0, 0, 0, 0]
  }
  if (isFirst) return [0, 0, r, r]
  if (isLast) return [r, r, 0, 0]
  return [0, 0, 0, 0]
}

/** Compute max stacked value and equally spaced ticks from 0 for vertical (value) axis. */
function getEqualSpacedTicks(
  data: DashboardStackedBarChartDataPoint[],
  series: DashboardStackedBarChartSeries[],
  tickCount = 6,
): { domainMax: number; ticks: number[] } {
  const dataKeys = series.map((s) => s.dataKey)
  let max = 0
  for (const row of data) {
    const sum = dataKeys.reduce((acc, key) => acc + (Number(row[key]) || 0), 0)
    if (sum > max) max = sum
  }
  if (max <= 0) return { domainMax: 100, ticks: [0, 20, 40, 60, 80, 100] }
  // Step in 1000s so labels are round; domainMax = step * (tickCount-1) for equal spacing
  const step = Math.ceil(max / (tickCount - 1) / 1000) * 1000 || 1000
  const domainMax = step * (tickCount - 1)
  const ticks = Array.from({ length: tickCount }, (_, i) => i * step)
  return { domainMax, ticks }
}

/** Reserve enough horizontal space for currency-style Y-axis labels (e.g. `$400K`). */
function getVerticalYAxisWidth(
  ticks: number[],
  formatTick: (value: number) => string,
  override?: number,
): number {
  if (override != null) return override
  if (!ticks.length) return 44
  const maxLen = Math.max(...ticks.map((t) => formatTick(t).length))
  return Math.min(64, Math.max(44, 12 + maxLen * 9))
}

function DashboardStackedBarChart({
  data,
  series,
  horizontal = true,
  categoryIndicatorColors,
  hideCategoryIndicatorCircles = false,
  hideCategoryAxisLabels = false,
  onBarClick,
  valueFormatter = (v) => String(v),
  height = 200,
  fillHeight = false,
  yAxisDomain,
  yAxisTicks,
  margin: marginOverride,
  xAxisInterval,
  yAxisWidth,
  yAxisTickMargin,
  xAxisTickMargin,
  stacked = true,
  barGap,
  barCategoryGap,
  legendIconType = 'circle',
  xAxisDomain,
  xAxisTicks,
  goalLineValue,
  showLegend = true,
  barSize: barSizeProp,
  hideZeroBars = false,
  collapseZeroBarsInGrouped = false,
  roundBarTopOnly = false,
  className = '',
}: DashboardStackedBarChartProps): React.ReactElement {
  const formatTick = (value: number): string => valueFormatter(value)
  const formatTooltip = (value: number): string => valueFormatter(value)
  const singleColorMode = Boolean(categoryIndicatorColors)
  const groupedVerticalRadius =
    roundBarTopOnly && !horizontal && !stacked
      ? VERTICAL_BAR_RADIUS_TOP
      : VERTICAL_BAR_RADIUS_ALL
  /** External legend sits below the chart — tighten bottom axis spacing. */
  const externalLegendLayout = !showLegend && !horizontal
  const useCollapsedGrouped =
    Boolean(collapseZeroBarsInGrouped) &&
    !stacked &&
    !horizontal &&
    Boolean(hideZeroBars)

  const chartData = useCollapsedGrouped
    ? data.map((row) => {
        let maxVal = 0
        const _bars: Array<{
          dataKey: string
          name: string
          color: string
          value: number
        }> = []
        series.forEach((s) => {
          const v = Number(row[s.dataKey]) || 0
          if (v > 0) _bars.push({ ...s, value: v })
          if (v > maxVal) maxVal = v
        })
        return { ...row, value: maxVal, _bars }
      })
    : data

  const { domainMax, ticks } = horizontal
    ? {
        domainMax: xAxisDomain?.[1] ?? 100,
        ticks: xAxisTicks ?? [0, 100],
      }
    : yAxisDomain != null && yAxisTicks != null
      ? { domainMax: yAxisDomain[1], ticks: yAxisTicks }
      : getEqualSpacedTicks(data, series, 4)

  const resolvedYAxisWidth = horizontal
    ? (yAxisWidth ?? 64)
    : getVerticalYAxisWidth(ticks, formatTick, yAxisWidth)

  const chartMargin = {
    ...(horizontal
      ? { top: 8, right: 8, left: 8, bottom: 8 }
      : {
          top: 8,
          right: 8,
          left: 0,
          bottom: externalLegendLayout ? (hideCategoryAxisLabels ? 4 : 2) : 12,
        }),
    ...marginOverride,
  }
  const categoryAxisTickMargin =
    xAxisTickMargin ??
    (hideCategoryAxisLabels ? 0 : externalLegendLayout ? 8 : 12)
  const categoryAxisHeight =
    hideCategoryAxisLabels && externalLegendLayout ? 0 : undefined
  const chart = (
    <RechartsBarChart
      data={chartData}
      layout={horizontal ? 'vertical' : 'horizontal'}
      margin={chartMargin}
      barGap={barGap}
      barCategoryGap={barCategoryGap}
    >
      {horizontal ? (
        <>
          {/* Vertical grid lines at each value tick (amount axis) */}
          <CartesianGrid
            strokeDasharray="0"
            horizontal={false}
            vertical
            stroke="var(--gray-100)"
          />
          <XAxis
            type="number"
            domain={xAxisDomain ?? [0, domainMax]}
            ticks={xAxisTicks ?? ticks}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'var(--gray-500)' }}
            tickFormatter={formatTick}
          />
          <YAxis
            type="category"
            dataKey="label"
            axisLine={false}
            tickLine={false}
            width={resolvedYAxisWidth}
            tick={(props) => <HorizontalCategoryAxisTick {...props} />}
          />
          {goalLineValue != null && (
            <ReferenceLine
              x={goalLineValue}
              stroke="var(--blue-500)"
              strokeDasharray="4 4"
              strokeWidth={2}
            />
          )}
        </>
      ) : (
        <>
          {/* Horizontal grid lines at each dollar (value) tick */}
          <CartesianGrid
            strokeDasharray="0"
            vertical={false}
            horizontal={true}
            stroke="var(--gray-100)"
          />
          <XAxis
            type="category"
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tickMargin={categoryAxisTickMargin}
            height={categoryAxisHeight}
            interval={xAxisInterval ?? 0}
            tick={
              hideCategoryAxisLabels
                ? false
                : ({ payload, x, y }) => (
                    <CategoryTick
                      payload={payload}
                      x={x}
                      y={y}
                      categoryIndicatorColors={categoryIndicatorColors}
                      hideCircle={hideCategoryIndicatorCircles}
                    />
                  )
            }
          />
          <YAxis
            type="number"
            domain={[0, domainMax]}
            ticks={ticks}
            interval={0}
            allowDuplicatedCategory={false}
            axisLine={false}
            tickLine={false}
            {...(yAxisTickMargin !== undefined
              ? { tickMargin: yAxisTickMargin }
              : {})}
            tick={{ fontSize: 12, fill: 'var(--gray-500)' }}
            tickFormatter={formatTick}
            width={resolvedYAxisWidth}
          />
          {goalLineValue != null && (
            <ReferenceLine
              y={goalLineValue}
              stroke="var(--blue-500)"
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{
                value: 'Goal',
                position: 'left',
                fontSize: 11,
                fill: 'var(--blue-500)',
              }}
            />
          )}
        </>
      )}
      <Tooltip
        content={
          singleColorMode ? (
            <ARTooltipContent valueFormatter={formatTooltip} />
          ) : useCollapsedGrouped ? (
            <CollapsedGroupedTooltipContent valueFormatter={formatTooltip} />
          ) : undefined
        }
        cursor={DASHBOARD_BAR_TOOLTIP_CURSOR}
        formatter={formatTooltip}
        contentStyle={{
          fontSize: 12,
          borderRadius: 8,
          border: '1px solid var(--gray-200)',
          backgroundColor: 'var(--gray-white)',
        }}
        itemStyle={{ color: 'var(--gray-700)' }}
        labelStyle={{ color: 'var(--gray-600)', fontWeight: 600 }}
        labelFormatter={(label) => String(label)}
      />
      {!singleColorMode &&
        showLegend &&
        (useCollapsedGrouped ? (
          <Legend
            content={() => (
              <CollapsedGroupedLegend
                series={series}
                iconType={legendIconType}
              />
            )}
          />
        ) : (
          <Legend
            wrapperStyle={{ fontSize: 12, color: 'var(--gray-600)' }}
            formatter={(value) => value}
            iconType={legendIconType}
            iconSize={10}
          />
        ))}
      {singleColorMode && series[0] ? (
        <Bar
          dataKey={series[0].dataKey}
          name=""
          fill="var(--gray-400)"
          radius={
            roundBarTopOnly && !horizontal
              ? VERTICAL_BAR_RADIUS_TOP
              : VERTICAL_BAR_RADIUS_ALL
          }
          barSize={horizontal ? undefined : (barSizeProp ?? 64)}
          maxBarSize={horizontal ? 24 : undefined}
          onClick={
            onBarClick
              ? (payload) =>
                  onBarClick(payload as DashboardStackedBarChartDataPoint)
              : undefined
          }
          cursor={onBarClick ? 'pointer' : undefined}
        >
          {data.map((entry, index) => (
            <Cell
              key={entry.label ?? index}
              fill={
                categoryIndicatorColors?.[String(entry.label)] ??
                'var(--gray-400)'
              }
            />
          ))}
        </Bar>
      ) : useCollapsedGrouped ? (
        <Bar
          dataKey="value"
          name=""
          fill="transparent"
          barSize={horizontal ? undefined : (barSizeProp ?? 999)}
          maxBarSize={horizontal ? undefined : 999}
          shape={CollapsedGroupedBarShape}
          isAnimationActive={false}
        />
      ) : (
        series.map((s, seriesIndex) => {
          const radius = stacked
            ? getStackedBarRadius(seriesIndex, series.length, horizontal)
            : groupedVerticalRadius
          return (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name}
              stackId={stacked ? 'stack' : undefined}
              fill={s.color}
              radius={radius}
              barSize={
                horizontal ? undefined : (barSizeProp ?? (stacked ? 64 : 28))
              }
              maxBarSize={horizontal ? 24 : undefined}
              shape={
                !stacked && !horizontal
                  ? hideZeroBars
                    ? HideZeroGroupedBarShape
                    : GroupedBarShape
                  : undefined
              }
            />
          )
        })
      )}
    </RechartsBarChart>
  )

  return (
    <div
      className={`dashboard-stacked-bar-chart${fillHeight ? ' dashboard-stacked-bar-chart--fill-height' : ''} ${className}`}
      style={fillHeight ? { height: '100%', minHeight: height } : { height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        {chart}
      </ResponsiveContainer>
    </div>
  )
}

export default DashboardStackedBarChart
